/**
 * The transport, assembled.
 *
 * One socket, one subscription registry, one sequencer, one dedupe set, one
 * buffer per channel and one `requestAnimationFrame` flush. Every rule the other
 * files enforce meets here, and the shape of the data path is the point:
 *
 * ```
 * socket ─▶ sequencer.check ─▶ dedupe ─▶ channel buffer      (per frame, O(1))
 *                                              │
 *                                        rAF flush ─▶ stores ─▶ notify()   (once)
 * ```
 *
 * **Nothing on the left half of that diagram calls `setState`.** The socket must
 * keep draining — a client that stops reading gets `SLOW_CONSUMER` and loses its
 * subscription — so frame receipt is an integer compare, a map lookup and a push.
 * Everything expensive happens once per animation frame, on the `default` lane,
 * where it cannot starve the countdown (plan §2.4).
 *
 * **Recovery does not compare a REST `seq` with a WS `seq`.** `asyncapi` §5.5 is
 * explicit that `seq` is per subscription, starts at 1 and resets on every
 * resubscribe, so a number from a REST body is not in the same space as a number
 * on a frame; docs/04 §9.2 and `openapi.OrderBook.seq` describe a book-global
 * position instead, and the two readings cannot both be right (register §F15a).
 * The procedure implemented here is the one `asyncapi` spells out and which is
 * correct under either reading: stop applying, unsubscribe, resubscribe with
 * `snapshot: true`, anchor on the new `ack`, and let the in-band snapshot be the
 * base. A REST snapshot is still fetched when a source is wired, to repaint
 * immediately — but it repaints a panel that stays marked stale until the socket
 * says otherwise.
 */

import type { Clock } from '@/lib/clock'
import { BufferSet, type ChannelBuffer } from '@/lib/buffer'
import { sharedScheduler, type RafScheduler } from '@/lib/raf'
import { StoreSet } from '@/stores'
import type {
  AnyServerFrame,
  ChannelName,
  ChannelFrame,
  HeartbeatData,
  ServerFrame,
  SubscribeAckData,
  UnsubscribeAckData,
  WsProblemData,
} from './protocol'
import { parseChannel, type ParsedChannel } from './channels'
import { WsConnection, type ConnectionStatus, type SocketFactory, type WsConfig } from './connection'
import { Heartbeat, type VisibilitySource } from './heartbeat'
import { Subscriptions, type SubscribeOptions } from './subscriptions'
import { Sequencer, type ChannelSyncState, type RecoveryRequest } from './sequencer'
import { DedupeSet } from './dedupe'

export interface SnapshotSource {
  /**
   * Fetch the REST counterpart of a channel (docs/03 Appendix A) and return it
   * as a `snapshot` frame body, or `null` if this channel has no counterpart.
   *
   * Returning a frame body rather than a bespoke shape means the recovery path
   * and the socket path apply through the same code — a second apply path is a
   * second place for the ladder to be assembled slightly differently.
   */
  (channel: ParsedChannel): Promise<unknown | null>
}

export interface WsClientHandlers {
  onStatus?: (status: ConnectionStatus) => void
  onChannelState?: (channel: ChannelName, state: ChannelSyncState) => void
  /** The session behind this connection expired. Private subscriptions are
   *  gone; the socket and every public subscription are untouched. */
  onSessionExpired?: (channels: ChannelName[]) => void
  /** A `heartbeat` frame's `config_revision` differs from the held one —
   *  re-fetch `GET /config`. */
  onConfigRevision?: (revision: number) => void
  /** Every `error` frame, after routing. For the toast layer. */
  onProblem?: (problem: WsProblemData) => void
  /**
   * Every server `heartbeat`, after the config-revision compare above.
   *
   * The transport interprets `config_revision` and nothing else on this frame:
   * `mode` is the kill switch and `venue_status` is the rolled-up health, and
   * both are product state that belongs to whoever renders the ticket and the
   * banner (§9.2.12).
   */
  onHeartbeat?: (data: HeartbeatData) => void
  /** The tab became visible after `hiddenMs`. docs/03 §9.2.4 is the caller's
   *  checklist: re-sync the clock over 60 s, re-snapshot, check the roll. */
  onVisible?: (hiddenMs: number) => void
  /** The tab was hidden. The ping cadence is this file's business; the depth
   *  channels are the caller's (§9.2.4). */
  onHidden?: () => void
  onFlush?: (info: { channels: number; frames: number; stores: number }) => void
}

/**
 * How long the caller wants control frames held back, in milliseconds.
 *
 * `0` means "send now". Anything above it is the market boundary: §6.0 Fact 2 is
 * that all 3,000 tabs reach `T+60` inside one clock error of each other, so
 * **nothing may be fetched, subscribed or negotiated there** — and a `seq` gap
 * at `T+59.9`, which is the busiest instant of the minute and therefore where a
 * slow gateway actually drops frames, made every affected tab unsubscribe and
 * resubscribe in exactly that window (`docs/17`).
 *
 * A callback rather than a period, because this file knows nothing about
 * markets and must not start: the runtime holds the anchor and answers. Same
 * shape and the same reason as `SnapshotSource`.
 */
export interface BoundaryGuard {
  (): number
}

export interface WsClientOptions extends WsClientHandlers {
  config: WsConfig
  clock: Clock
  socketFactory?: SocketFactory
  scheduler?: RafScheduler
  snapshotSource?: SnapshotSource
  boundaryGuard?: BoundaryGuard
  visibility?: VisibilitySource
  random?: () => number
  monoNow?: () => number
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (handle: number) => void
}

export class WsClient {
  readonly connection: WsConnection
  readonly subscriptions: Subscriptions
  readonly sequencer: Sequencer
  readonly heartbeat: Heartbeat
  readonly dedupe: DedupeSet
  readonly stores: StoreSet
  readonly buffers = new BufferSet()

  readonly #handlers: WsClientHandlers
  readonly #scheduler: RafScheduler
  readonly #snapshotSource: SnapshotSource | undefined
  readonly #boundaryGuard: BoundaryGuard | undefined
  readonly #config: WsConfig
  readonly #setTimer: (fn: () => void, ms: number) => number
  readonly #clearTimer: (handle: number) => void
  /** Armed when a recovery asks for a resubscribe, cleared by the `ack` that
   *  answers it. A channel left in this map is one nothing is repairing. */
  readonly #recoveryTimers = new Map<ChannelName, number>()
  readonly #recovering = new Set<ChannelName>()
  /** Recoveries waiting out a market boundary — see `BoundaryGuard`. Held apart
   *  from `#recoveryTimers`, which time out a resubscribe that was *sent*. */
  readonly #deferred = new Map<ChannelName, number>()

  #flushScheduled = false
  #configRevision: number | null = null
  #framesReceived = 0
  #framesDropped = 0

  constructor(options: WsClientOptions) {
    this.#handlers = options
    this.#config = options.config
    this.#scheduler = options.scheduler ?? sharedScheduler()
    this.#snapshotSource = options.snapshotSource
    this.#boundaryGuard = options.boundaryGuard
    this.#setTimer = options.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimer = options.clearTimer ?? ((handle) => { globalThis.clearTimeout(handle) })
    this.stores = new StoreSet({ checksumLevels: options.config.checksum_levels })
    this.dedupe = new DedupeSet({ now: options.monoNow ?? (() => performance.now()) })

    this.connection = new WsConnection({
      config: options.config,
      clock: options.clock,
      ...(options.socketFactory ? { socketFactory: options.socketFactory } : {}),
      ...(options.random ? { random: options.random } : {}),
      ...(options.monoNow ? { monoNow: options.monoNow } : {}),
      ...(options.setTimer ? { setTimer: options.setTimer } : {}),
      ...(options.clearTimer ? { clearTimer: options.clearTimer } : {}),
      onOpen: () => this.#onOpen(),
      onClose: () => this.#onClose(),
      onFrame: (frame) => this.#onFrame(frame),
      onStatus: (status) => this.#handlers.onStatus?.(status),
    })

    this.subscriptions = new Subscriptions({
      connection: this.connection,
      onSubscribed: (entries) => {
        for (const entry of entries) this.sequencer.anchor(entry.channel, entry.seq)
      },
      onDropped: (channels) => {
        for (const channel of channels) this.buffers.get(channel)?.clear()
      },
    })

    this.sequencer = new Sequencer({
      onRecoveryNeeded: (request) => {
        void this.#recover(request)
      },
      onStateChange: (channel, state) => {
        // The `STALE` pill and the order-entry gate read this. A book the client
        // knows it is not receiving updates for must never render as if it were
        // right, and an order placed against one is a guess (docs/03 §9.2.1).
        const descriptor = parseChannel(channel)
        if (descriptor.kind === 'book' && descriptor.marketId) {
          this.stores.bookFor(descriptor.marketId).markStale(state !== 'SYNCED')
        }
        // Every route out of `RECOVERING` ends here — the resubscribe's `ack`,
        // an in-band snapshot, a reconnect's `reset()` — so this is the one
        // place the stall timer can be disarmed without enumerating them.
        if (state !== 'RECOVERING') this.#disarmRecoveryTimeout(channel)
        this.#handlers.onChannelState?.(channel, state)
      },
    })

    this.heartbeat = new Heartbeat({
      connection: this.connection,
      ...(options.visibility ? { visibility: options.visibility } : {}),
      ...(options.monoNow ? { monoNow: options.monoNow } : {}),
      ...(options.setTimer ? { setTimer: options.setTimer } : {}),
      ...(options.clearTimer ? { clearTimer: options.clearTimer } : {}),
      onDead: () => {
        // Two missed pongs. Close rather than wait for a TCP timeout that can
        // take minutes — on a 60-second market, minutes is several markets. The
        // close handler schedules the backoff, so there is one reconnect path —
        // and `restart` detaches the dead socket first, because its own
        // `onclose` lands after the replacement has opened.
        this.connection.restart(1000, 'no pong')
      },
      onHeartbeat: (data) => {
        this.#handlers.onHeartbeat?.(data)
        if (typeof data.config_revision !== 'number') return
        if (this.#configRevision !== null && data.config_revision !== this.#configRevision) {
          this.#handlers.onConfigRevision?.(data.config_revision)
        }
        this.#configRevision = data.config_revision
      },
      onVisible: (hiddenMs) => this.#handlers.onVisible?.(hiddenMs),
      onHidden: () => this.#handlers.onHidden?.(),
    })
  }

  get status(): ConnectionStatus {
    return this.connection.status
  }

  get framesReceived(): number {
    return this.#framesReceived
  }

  /** Frames the sequencer refused: duplicates, gapped frames, and frames for a
   *  channel under recovery. A climbing count is a stream in trouble. */
  get framesDropped(): number {
    return this.#framesDropped
  }

  connect(): void {
    this.connection.connect()
  }

  close(): void {
    this.heartbeat.stop()
    this.connection.close()
  }

  subscribe(channels: readonly ChannelName[], options?: SubscribeOptions): void {
    this.subscriptions.subscribe(channels, options)
  }

  unsubscribe(channels: readonly ChannelName[]): void {
    this.subscriptions.unsubscribe(channels)
    for (const channel of channels) {
      this.buffers.delete(channel)
      this.sequencer.forget(channel)
    }
  }

  /** One batched frame each way — the roll, and the asset switch. */
  setDesired(channels: readonly ChannelName[], options?: SubscribeOptions): void {
    this.subscriptions.setDesired(channels, options)
  }

  /** Re-authenticate an already-open socket. Dropping and reopening it to add
   *  private channels would blank every public panel for a round trip, and at
   *  60-second markets there is no moment where that is cheap. */
  authenticate(token: string): string | null {
    return this.connection.send('auth', { token })
  }

  // -------------------------------------------------------------------------

  #onOpen(): void {
    // A new connection starts empty: no session resumption, no `seq` continuation.
    for (const channel of [...this.#recoveryTimers.keys()]) this.#disarmRecoveryTimeout(channel)
    this.#clearDeferred()
    this.sequencer.reset()
    this.buffers.clear()
    this.#recovering.clear()
    this.heartbeat.start()
    // The desired set is the client's intent and survived the outage. One
    // batched `subscribe` restores it, chunked only at the 50-channel ceiling.
    this.subscriptions.resync()
  }

  #onClose(): void {
    this.heartbeat.stop()
    for (const channel of [...this.#recoveryTimers.keys()]) this.#disarmRecoveryTimeout(channel)
    // A recovery waiting out a boundary is about a stream that no longer exists.
    // `resync()` on the next open is the repair, and it is one batched frame.
    this.#clearDeferred()
    this.sequencer.reset()
    // Panels keep their last known values behind a `STALE` pill. Nothing blanks:
    // the countdown is local and monotonic and does not need the socket, which
    // is why the page still looks alive while this is happening.
    for (const book of this.stores.books.values()) book.markStale(true)
    this.stores.notifyAll()
  }

  #onFrame(frame: AnyServerFrame): void {
    this.#framesReceived += 1
    // Liveness and the clock first, and unconditionally: a `pong` is the
    // unbiased offset sample and a `heartbeat` is the stall detector, and
    // neither may be gated on anything downstream of them.
    this.heartbeat.observe(frame)
    if (frame.type === 'pong') return
    if (frame.type === 'heartbeat') {
      // Still sequence-checked. A gap in `heartbeat` proves frames were dropped
      // even on a socket that is otherwise idle, which is the only evidence
      // available when nothing is subscribed.
      this.#onDataFrame(frame)
      return
    }

    switch (frame.type) {
      case 'ack': {
        // An `auth` ack is the one ack that is not about subscriptions, and it
        // is the moment a connection becomes able to serve the private half.
        // Anything the client still wants and does not hold is asked for again
        // here — which is what makes "signed in" and "receiving private data"
        // the same state rather than two that have to be sequenced correctly by
        // every caller (register §F23a).
        //
        // `authenticated` is `AuthAckData`'s own required member, so this needs
        // no request-id bookkeeping to tell the two acks apart.
        if ((frame.data as { authenticated?: unknown } | undefined)?.authenticated === true) {
          this.subscriptions.resubscribeMissing()
          return
        }
        this.subscriptions.applyAck(frame as ServerFrame<SubscribeAckData | UnsubscribeAckData>)
        return
      }
      case 'error':
        this.#onProblem(frame as ServerFrame<WsProblemData>)
        return
      default:
        this.#onDataFrame(frame as ChannelFrame)
    }
  }

  #onDataFrame(frame: ChannelFrame): void {
    if (typeof frame.channel !== 'string') {
      this.#framesDropped += 1
      return
    }

    const verdict = this.sequencer.check(frame)
    if (verdict !== 'APPLY' && verdict !== 'UNSEQUENCED') {
      this.#framesDropped += 1
      return
    }

    const descriptor = parseChannel(frame.channel)
    if (descriptor.private && frame.type !== 'snapshot') {
      // At-least-once. The same fill arrives again across a reconnect, a
      // failover or a re-auth, and a client that assumes otherwise double-counts
      // it. A snapshot is exempt: it is the seed, not a redelivery.
      const accepted = this.dedupe.accept(frame.channel, frame.data)
      if (accepted === 'STALE' || accepted === 'DUPLICATE') {
        this.#framesDropped += 1
        return
      }
    }

    this.#buffer(frame)
  }

  #buffer(frame: ChannelFrame): void {
    const channel = frame.channel
    const buffer = this.#bufferFor(channel)
    buffer.push(frame)
    // A buffer that overflowed lost a frame the sequence depended on. The book
    // is missing a delta and only a snapshot can say what it should hold — the
    // same answer as a server-side gap, because the ladder cannot tell them
    // apart and neither should the recovery path.
    if (buffer.lossy) this.sequencer.noteBufferLoss(channel)
    this.#scheduleFlush()
  }

  #bufferFor(channel: ChannelName): ChannelBuffer<ChannelFrame> {
    const descriptor = parseChannel(channel)
    return this.buffers.channel<ChannelFrame>(channel, {
      mode: descriptor.coalesce,
      ...(descriptor.coalesce === 'keyed'
        ? {
            keyOf: (frame: ChannelFrame) => {
              const data = frame.data as Record<string, unknown> | undefined
              const key = descriptor.bufferKey ? data?.[descriptor.bufferKey] : undefined
              // A `keyed` buffer with no key would coalesce two different rows
              // into one. Falling back to the frame's own `seq` keeps every
              // frame distinct, which is the safe direction.
              return key === undefined ? `seq:${frame.seq ?? 0}` : String(key)
            },
          }
        : {}),
    })
  }

  #onProblem(frame: ServerFrame<WsProblemData>): void {
    const problem = frame.data
    if (!problem) return
    this.#handlers.onProblem?.(problem)

    if (problem.code === 'RATE_LIMITED' && typeof problem.retry_after_ms === 'number') {
      this.connection.noteRetryAfter(problem.retry_after_ms)
    }

    const dropped = problem.unsubscribed ?? (problem.channel ? [problem.channel as ChannelName] : [])
    if (problem.code === 'SESSION_EXPIRED') {
      // The socket is untouched and every public subscription keeps flowing.
      // Only the private half goes dark, and the caller re-auths in place.
      this.subscriptions.markDropped(dropped, 'SESSION_EXPIRED')
      for (const channel of dropped) this.sequencer.forget(channel)
      this.#handlers.onSessionExpired?.([...dropped])
      return
    }

    if (problem.code === 'SERVER_SHUTDOWN') {
      // The second of the two drops the server names as fleet-wide. `asyncapi`
      // describes it as "rolling deploy or operator restart. Every subscription
      // is gone" — so the close that follows must not be treated as this tab's
      // own bad luck, which is what the stable-reset would do for every tab at
      // once (`WsConnection.FLEET_EVENT_ATTEMPT`, `docs/17`).
      this.connection.noteFleetEvent()
    }

    if (problem.terminal === true) {
      this.subscriptions.markDropped(dropped, 'TERMINAL_ERROR')
      for (const channel of dropped) this.sequencer.noteTerminalError(channel)
    }
  }

  // -------------------------------------------------------------------------

  /**
   * The recovery procedure (asyncapi §5.5).
   *
   * One at a time per channel: a gap detected on the frame after the one that
   * started a recovery would otherwise queue a second, and at 20 Hz that is a
   * request storm rather than a repair.
   */
  async #recover(request: RecoveryRequest): Promise<void> {
    const { channel } = request
    if (this.#recovering.has(channel)) return
    this.#recovering.add(channel)

    try {
      this.sequencer.beginRecovery(channel)
      // Buffered frames belong to a stream that is about to be replaced.
      this.buffers.get(channel)?.clear()
      const descriptor = parseChannel(channel)

      if (descriptor.kind === 'heartbeat') {
        // Nothing to re-snapshot and nothing to resubscribe: the beat is
        // unsolicited, arrives every 5 s, and every one of them is complete. The
        // gap is worth *recording* — it is proof frames were dropped — and the
        // next beat repairs it for free.
        this.sequencer.endRecovery(channel)
        return
      }

      if (this.#snapshotSource) {
        // Optional and best-effort: it repaints immediately, it does not decide
        // anything. The panel stays stale until the socket's own snapshot lands.
        try {
          const body = await this.#snapshotSource(descriptor)
          if (body != null) {
            this.stores.apply(channel, [
              { type: 'snapshot', channel, server_time_ms: 0, data: body } as ChannelFrame,
            ])
            this.stores.notifyAll()
          }
        } catch {
          // A failed REST snapshot changes nothing: the resubscribe below is the
          // path that actually repairs the stream.
        }
      }

      if (!this.connection.isOpen) {
        // **No resubscribe means no `ack`, and no `ack` means nothing ever
        // ends this recovery.** Left `RECOVERING`, the sequencer drops every
        // frame this channel receives for the life of the connection while the
        // REST snapshot above makes the panel look repaired. Give it up as
        // `STALE` instead: the pill stays, the order-entry gate keeps blocking,
        // and `#onOpen`'s `reset()` + `resync()` is the repair that actually
        // arrives.
        this.sequencer.abandonRecovery(channel)
        return
      }

      // **The roll may have dropped this channel while the snapshot was in
      // flight**, and resubscribing then resurrects a subscription the tab has
      // deliberately torn down: `setDesired` diffs against its own record, so
      // the channel is held, streaming, and invisible to the runtime until the
      // next `#applyHolds` happens to drop it again — and `StoreSet` recreates
      // the store `dropMarketDepth` had just deleted, for a market whose own
      // teardown has already fired and will not fire twice (`docs/17`).
      //
      // Checked **here**, after the await, because that window is the whole
      // defect: at entry the channel was desired or there would have been no
      // frame to gap.
      if (!this.subscriptions.desiredChannels.includes(channel)) {
        this.sequencer.abandonRecovery(channel)
        return
      }

      // §6.0 Fact 2 — see `BoundaryGuard`. A gap detected just before `T+60`
      // waits for the boundary to pass rather than negotiating across it. The
      // channel stays `RECOVERING`, which is what the ticket's `SOCKET_STALE`
      // gate already reads, so the wait is visible rather than silent.
      const holdMs = this.#boundaryGuard?.() ?? 0
      if (holdMs > 0) {
        // **It stays in `#recovering` across the hold**, which is why this is a
        // second map rather than a bare timer: `#recovering` is what makes the
        // procedure one-at-a-time per channel — "a gap detected on the frame
        // after the one that started a recovery would otherwise queue a second,
        // and at 20 Hz that is a request storm rather than a repair" — and the
        // `finally` below would have released it for the whole of the wait, at
        // the one instant of the minute where a second is least affordable.
        this.#deferred.set(
          channel,
          this.#setTimer(() => {
            this.#deferred.delete(channel)
            this.#recovering.delete(channel)
            void this.#recover(request)
          }, holdMs),
        )
        return
      }

      this.subscriptions.unsubscribe([channel])
      this.subscriptions.subscribe([channel], { snapshot: true })
      this.#armRecoveryTimeout(channel)
    } finally {
      if (!this.#deferred.has(channel)) this.#recovering.delete(channel)
    }
  }

  /**
   * A resubscribe that is never acknowledged is the same defect one layer over.
   *
   * The socket was open when the recovery asked, so nothing else is going to
   * notice: no close fires, no reconnect resyncs, and the channel sits
   * `RECOVERING` behind a book that stopped moving. One bounded wait, then the
   * channel is given up as `STALE` and the whole procedure is run again — at
   * this cadence, which is the point: `RECOVERY_STALLED` retries at seconds,
   * not at the 20 Hz the "one recovery at a time" rule exists to prevent.
   */
  #armRecoveryTimeout(channel: ChannelName): void {
    this.#disarmRecoveryTimeout(channel)
    this.#recoveryTimers.set(
      channel,
      this.#setTimer(() => {
        this.#recoveryTimers.delete(channel)
        if (this.sequencer.stateOf(channel) !== 'RECOVERING') return
        this.sequencer.abandonRecovery(channel)
        if (!this.connection.isOpen) return
        void this.#recover({
          channel,
          reason: 'RECOVERY_STALLED',
          lastSeq: this.sequencer.get(channel)?.lastSeq ?? null,
        })
      }, this.#recoveryAckTimeoutMs()),
    )
  }

  /**
   * How long a resubscribe may go unacknowledged before the channel is given up.
   *
   * Derived from `config.ws.ping_interval_ms` rather than named as a number,
   * because nothing in `config.ws` is a constant in this client (plan §2.12) and
   * this is a statement about the same round trip the beat measures: two beats
   * with no `ack` is a server that is not answering this subscription. The floor
   * is there for a venue that configures a very fast ping — the timeout is a
   * backstop, not a second heartbeat.
   */
  #recoveryAckTimeoutMs(): number {
    return Math.max(2_000, this.#config.ping_interval_ms * 2)
  }

  #clearDeferred(): void {
    for (const handle of this.#deferred.values()) this.#clearTimer(handle)
    this.#deferred.clear()
  }

  #disarmRecoveryTimeout(channel: ChannelName): void {
    const handle = this.#recoveryTimers.get(channel)
    if (handle === undefined) return
    this.#clearTimer(handle)
    this.#recoveryTimers.delete(channel)
  }

  // -------------------------------------------------------------------------

  #scheduleFlush(): void {
    if (this.#flushScheduled) return
    this.#flushScheduled = true
    // `default` lane: the countdown and `<ResolutionPanel>` hold the `critical`
    // one and are never starved by book churn (plan §2.4).
    this.#scheduler.scheduleOnce(() => this.flush(), 'default')
  }

  /**
   * Drain every pending buffer into the stores and publish once.
   *
   * Public because the headless harness drives it directly — a test that had to
   * wait for real animation frames to check a book would be a test nobody runs.
   */
  flush(): void {
    this.#flushScheduled = false
    const channels = this.buffers.pendingChannels()
    let frames = 0

    for (const name of channels) {
      const channel = name as ChannelName
      const buffer = this.buffers.get(channel)
      if (!buffer) continue
      const drained = buffer.drain() as ChannelFrame[]
      if (drained.length === 0) continue
      frames += drained.length

      const result = this.stores.apply(channel, drained)
      for (const verdict of result.checksums) {
        // `seq` proved no frame was lost; this proves none was misapplied. A
        // mismatch takes the identical recovery path — the ladder is equally
        // wrong either way.
        this.sequencer.verifyChecksum(verdict.channel, verdict.expected, verdict.computed)
      }
    }

    const stores = this.stores.notifyAll()
    this.#handlers.onFlush?.({ channels: channels.length, frames, stores })
  }

  /** `config.ws`, for callers that need a limit rather than a constant. */
  get config(): WsConfig {
    return this.#config
  }
}
